import { useEffect, useState } from 'react'
import { motion } from 'motion/react'
import {
  Cpu, Wifi, Shield, Clock,
  Map as MapIcon, Crosshair, Users, HardDrive,
} from 'lucide-react'
import { MarketingLayout } from '../components/MarketingLayout'
import { SEO } from '../components/SEO'
import { CTABanner } from '../components/CTABanner'
import { FAQ } from '../components/FAQ'
import { PlansSection } from '../components/PlansSection'
import { FeatureGrid } from '../components/FeatureCard'
import { type Plan, type PlansResponse } from '../types'

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Voltary'
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || ''

const features = [
  {
    icon: <Cpu size={22} />,
    title: 'CPU dedicada',
    desc: 'Tickrate estable (100 fps del servidor) incluso con 32 jugadores disparándose a la vez.',
  },
  {
    icon: <HardDrive size={22} />,
    title: 'SSD NVMe',
    desc: 'Carga de mapas instantánea. Sin esperas en rondas largas con mapas grandes.',
  },
  {
    icon: <Wifi size={22} />,
    title: 'Latencia < 30ms',
    desc: 'Servidores en Europa y América. Ping bajo para que cada disparo registre a tiempo.',
  },
  {
    icon: <MapIcon size={22} />,
    title: 'Cambio de mapa en caliente',
    desc: 'Cambia de mapa sin reiniciar el servidor. Los jugadores siguen conectados durante el cambio.',
  },
  {
    icon: <Shield size={22} />,
    title: 'Anti-cheat',
    desc: 'Detección básica de cheats comunes integrada. Tu servidor se mantiene limpio sin configuración extra.',
  },
  {
    icon: <Shield size={22} />,
    title: 'Protección DDoS',
    desc: 'Tus partidas no se cortan por ataques. La capa de mitigación está incluida.',
  },
]

const faqs = [
  {
    q: '¿Qué versión de Counter-Strike soportáis?',
    a: 'Soportamos Counter-Strike 1.6 (Half-Life 1 engine) y Counter-Strike: Source (Source engine). Eliges la versión al crear el servidor.',
  },
  {
    q: '¿Cuántos jugadores soporta el servidor?',
    a: 'CS 1.6 y CS:S soportan entre 16-32 jugadores por servidor',
  },
  {
    q: '¿Puedo instalar AMX Mod X o plugins?',
    a: 'No por ahora. Nuestro servidor de Counter-Strike es una instancia estándar sin soporte de plugins. Soporte para AMX Mod X, Metamod y mods está en el roadmap y se añadirá en una versión futura.',
  },
  {
    q: '¿Puedo cambiar el mapa sin reiniciar el servidor?',
    a: 'Sí. Desde el panel de control puedes cambiar el mapa actual sin que los jugadores se desconecten. El servidor sigue corriendo durante el cambio.',
  },
  {
    q: '¿Qué mapas vienen preinstalados?',
    a: 'Vienen los mapas oficiales de CS 1.6 y CS:S (de_dust2, de_inferno, de_nuke, cs_office, etc.).',
  },
  {
    q: '¿Qué es el tickrate y por qué importa?',
    a: 'El tickrate es cuántas veces por segundo el servidor procesa el estado del juego. Tickrate 100 (estándar competitivo) significa que el servidor "piensa" 100 veces por segundo, lo que da un registro de daño y posición más preciso. Nuestro hardware mantiene tickrate 100 estable con 32 jugadores.',
  },
]

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Hosting de Counter-Strike 1.6 y Source - ${APP_NAME}`,
    description: 'Servidores dedicados de Counter-Strike 1.6 y CS:S con tickrate 100 estable. Cambio de mapa en caliente, anti-cheat básico y protección DDoS. Soporte para 32 jugadores.',
    provider: { '@type': 'Organization', name: APP_NAME },
    areaServed: { '@type': 'Place', name: 'España, Latinoamérica, Europa' },
    serviceType: 'Game Server Hosting',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://voltary.org/' },
      { '@type': 'ListItem', position: 2, name: 'Counter-Strike', item: 'https://voltary.org/counter-strike' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  },
]

interface CounterStrikeProps {
  initialPlans?: Plan[]
}

export function CounterStrike({ initialPlans = [] }: CounterStrikeProps) {
  const [plans, setPlans] = useState<Plan[]>(initialPlans)

  useEffect(() => {
    let cancelled = false
    fetch(`${BACKEND_URL}/api/servers/plans/${encodeURIComponent('Counter Strike')}`)
      .then((r) => r.json() as Promise<PlansResponse>)
      .then((data) => {
        if (!cancelled && Array.isArray(data.plans)) {
          setPlans(data.plans)
        }
      })
      .catch(() => {/* silencioso: mantenemos planes iniciales */})
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <MarketingLayout>
      <SEO
        title={`Hosting de Counter-Strike 1.6 y Source - ${APP_NAME}`}
        description="Servidores dedicados de Counter-Strike 1.6 y CS:S con tickrate 100 estable. Cambio de mapa en caliente, anti-cheat y protección DDoS incluida. Hasta 32 jugadores."
        canonical="https://voltary.org/counter-strike"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="cs-hero">
        <div className="cs-hero-bg" aria-hidden="true">
          <div className="cs-hero-glow cs-hero-glow--1" />
          <div className="cs-hero-glow cs-hero-glow--2" />
        </div>

        <motion.div
          className="cs-hero-content"
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } } }}
        >
          <motion.span
            className="cs-hero-eyebrow"
            variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
          >
            <Crosshair size={14} /> Counter-Strike 1.6 &amp; Source
          </motion.span>

          <motion.h1
            className="cs-hero-title"
            variants={{ hidden: { opacity: 0, y: 40 }, show: { opacity: 1, y: 0, transition: { duration: 0.8 } } }}
          >
            Servidores de<br />
            <span className="cs-hero-title-accent">Counter-Strike</span><br />
            <span>tickrate 100, sin lag</span>
          </motion.h1>

          <motion.p
            className="cs-hero-subtitle"
            variants={{ hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0, transition: { duration: 0.7 } } }}
          >
            CS 1.6 y CS:S en hardware dedicado. Tickrate estable con 32 jugadores,
            cambio de mapa en caliente, anti-cheat y protección DDoS.
            Configura en 2 minutos.
          </motion.p>

          <motion.div
            className="cs-hero-cta"
            variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
          >
            <motion.a
              href="/login"
              className="cs-hero-primary"
              whileHover={{ y: -3, scale: 1.02 }}
              whileTap={{ scale: 0.96 }}
            >
              Crear mi servidor CS →
            </motion.a>
            <motion.a
              href="#features"
              className="cs-hero-secondary"
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.96 }}
            >
              Ver características
            </motion.a>
          </motion.div>

          <motion.div
            className="cs-hero-trust"
            variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 0.8, delay: 0.3 } } }}
          >
            <span><Users size={14} /> Hasta 32 jugadores</span>
            <span><Clock size={14} /> Setup en 2 minutos</span>
            <span><Wifi size={14} /> Ping &lt; 30ms</span>
          </motion.div>
        </motion.div>
      </section>

      {/* FEATURES */}
      <section id="features" className="cs-section">
        <div className="cs-section-header">
          <span className="cs-section-tag">CARACTERÍSTICAS</span>
          <h2>Todo lo que tu servidor de CS necesita</h2>
          <p>Hardware optimizado para juegos de acción rápida: el tickrate no se cae ni en rondas de 5v5 cerradas.</p>
        </div>

        <FeatureGrid
          items={features.map((f) => ({
            icon: f.icon,
            title: f.title,
            description: f.desc,
          }))}
        />
      </section>

      <PlansSection plans={plans} title="Elige tu plan" />

      <FAQ items={faqs} title="Preguntas frecuentes sobre hosting de Counter-Strike" />

      <CTABanner
        title="Tu servidor de CS en 2 minutos"
        description="Empieza con crédito gratis. Tickrate 100, cambio de mapa en caliente y todo lo que necesitas para jugar."
        primaryLabel="Crear servidor"
        primaryHref="/login"
      />
    </MarketingLayout>
  )
}